import {
  telemetryInternalIds,
  telemetryIdsFromUrl,
} from './telemetry-identifiers';
export {
  sentryClientGuid,
  sentryClientGuidFromUrl,
} from './telemetry-identifiers';
import type { Breadcrumb, ErrorEvent, StackFrame } from '@sentry/angular';
import { API_ROUTE_TEMPLATES } from './structured-log-policy';

// Values crossing this boundary are literals from application code, never
// free-form client content. Adding a diagnostic field requires reviewing it here.
const sources = new Set([
  'createClientSession',
  'retireConversationlessRow',
  'MemberMetadataHeaderService',
]);
const messages = new Set([
  'Sentry verification: Angular event handler',
  'Sentry verification: browser error',
  'Sentry verification: unhandled rejection',
  'Credit report rendering failed',
  'Metadata header fetch failed',
  'Application operation failed',
  'HTTP request failed',
]);
const types = new Set([
  'Error',
  'TypeError',
  'ReferenceError',
  'SyntaxError',
  'RangeError',
  'URIError',
  'EvalError',
  'AggregateError',
  'DOMException',
  'HttpErrorResponse',
  'TimeoutError',
  'AbortError',
]);
const routes = [
  ...API_ROUTE_TEMPLATES,
  '/brightnav/admin/dev/sentry-logs',
  '/brightnav/admin/dev',
  '/brightnav/admin/suggestions',
  '/brightnav/admin/review',
  '/brightnav/admin',
  '/brightnav/home',
  '/brightnav/myclients',
  '/brightnav/conversation/:id',
  '/brightnav/conversation',
  '/brightnav/employee/:id',
  '/brightnav/employee',
  '/brightnav',
  '/dashboard',
  '/login/callback',
  '/login',
  '/',
];
const uuid = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;

/** Discard origin, query and fragment; emit only an explicitly known template. */
export function sentryRoute(value: unknown): string {
  if (typeof value !== 'string') return '/unclassified';
  try {
    const path = new URL(value, 'https://route.invalid').pathname;
    return (
      routes.find((route) =>
        new RegExp(`^${route.replace(/:[a-z]+/g, '[^/]+')}/?$`).test(path),
      ) ?? '/unclassified'
    );
  } catch {
    return '/unclassified';
  }
}

function method(value: unknown): string | undefined {
  return typeof value === 'string' &&
    /^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)$/i.test(value)
    ? value.toUpperCase()
    : undefined;
}

function status(value: unknown): number | undefined {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= 599
    ? value
    : undefined;
}

/** Only static summaries survive; stack locations distinguish unknown failures. */
function errorMessage(value: unknown): string {
  return typeof value === 'string' && messages.has(value)
    ? value
    : 'Application operation failed';
}

export function safeSentryExtras(
  input: Record<string, unknown> = {},
): Record<string, unknown> {
  const output: Record<string, unknown> = { ...telemetryInternalIds(input) };
  if (typeof input['source'] === 'string' && sources.has(input['source']))
    output['source'] = input['source'];
  if (
    typeof input['stage'] === 'string' &&
    [
      'load_pdfjs',
      'read_report',
      'load_document',
      'load_page',
      'extract_text',
      'create_canvas',
      'render_page',
    ].includes(input['stage'])
  )
    output['stage'] = input['stage'];
  if (
    typeof input['pageNumber'] === 'number' &&
    Number.isInteger(input['pageNumber']) &&
    input['pageNumber'] >= 1 &&
    input['pageNumber'] <= 10000
  )
    output['pageNumber'] = input['pageNumber'];
  if (typeof input['errorType'] === 'string' && types.has(input['errorType']))
    output['errorType'] = input['errorType'];
  const httpMethod = method(input['method']);
  if (httpMethod) output['method'] = httpMethod;
  if (typeof input['route_template'] === 'string')
    output['route_template'] = sentryRoute(input['route_template']);
  const httpStatus = status(input['http_status']);
  if (httpStatus !== undefined) output['http_status'] = httpStatus;
  if (typeof input['request_id'] === 'string' && uuid.test(input['request_id']))
    output['request_id'] = input['request_id'];
  return output;
}

export function filterSentryBreadcrumb(
  breadcrumb: Breadcrumb,
): Breadcrumb | null {
  const data = breadcrumb.data ?? {};
  const base = { timestamp: breadcrumb.timestamp, level: breadcrumb.level };
  switch (breadcrumb.category) {
    case 'fetch':
    case 'xhr':
    case 'api':
      return {
        ...base,
        category: breadcrumb.category,
        type: 'http',
        message: 'HTTP request',
        data: {
          method: method(data['method']),
          url: sentryRoute(data['url'] ?? data['path']),
          status_code: status(data['status_code']),
          ...telemetryIdsFromUrl(data['url'] ?? data['path']),
          ...telemetryInternalIds(data),
        },
      };
    case 'navigation':
      return {
        ...base,
        category: 'navigation',
        type: 'navigation',
        data: { from: sentryRoute(data['from']), to: sentryRoute(data['to']) },
      };
    case 'copilot': {
      const operation = data['operation'];
      if (operation !== 'fetch_conversation' && operation !== 'submit_message')
        return null;
      return {
        ...base,
        category: 'copilot',
        message: 'Copilot operation',
        data: { operation, ...telemetryInternalIds(data) },
      };
    }
    case 'system':
      return breadcrumb.message === 'BrightHub bootstrapped'
        ? { ...base, category: 'system', message: 'BrightHub bootstrapped' }
        : null;
    default:
      // Console arguments and DOM selectors can contain client text/identifiers.
      return null;
  }
}

/** Keep a build asset path without query strings, never a page URL or source text. */
function stackFile(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const clean = value.split(/[?#]/, 1)[0];
  // Bundled and source-mapped code locations; exclude dynamic file names/URLs.
  if (
    /^(?:webpack:\/\/[^/]*\/|\.\/)?(?:apps|libs|node_modules)\/[\w./@-]+\.[cm]?[jt]sx?$/.test(
      clean,
    )
  )
    return clean;
  try {
    const url = new URL(clean, 'https://asset.invalid');
    const ownOrigin =
      typeof location !== 'undefined' && url.origin === location.origin;
    const localOrigin = ['localhost', '127.0.0.1'].includes(url.hostname);
    if (
      (ownOrigin || localOrigin || !/^[a-z]+:\/\//i.test(clean)) &&
      /^\/(?:main|polyfills|runtime|vendor|scripts|common|styles|chunk|\d+)(?:[.-][\da-z]+)*\.[cm]?js$/i.test(
        url.pathname,
      )
    )
      // Keep the original asset origin/path for source-map lookup. Only known
      // bundle basenames survive; URL credentials, queries and hashes never do.
      return !/^[a-z]+:\/\//i.test(clean)
        ? clean.startsWith('//')
          ? url.pathname
          : clean
        : `${url.protocol}//${url.host}${url.pathname}`;
  } catch {
    /* Unknown locations are omitted. */
  }
  return undefined;
}

function stackFrame(frame: StackFrame): StackFrame {
  const filename = stackFile(frame.filename);
  return {
    filename,
    abs_path: stackFile(frame.abs_path),
    function:
      filename &&
      typeof frame.function === 'string' &&
      /^[\w.$<> [\]-]{1,160}$/.test(frame.function)
        ? frame.function
        : undefined,
    lineno: frame.lineno,
    colno: frame.colno,
    in_app: frame.in_app,
  };
}

/** Keep reviewed sampling fields used by the SDK in envelope headers. */
export function safeSentrySamplingMetadata(
  event: ErrorEvent,
): ErrorEvent['sdkProcessingMetadata'] {
  const input = event.sdkProcessingMetadata?.['dynamicSamplingContext'] as
    | Record<string, unknown>
    | undefined;
  if (!input || typeof input !== 'object') return undefined;
  const output: Record<string, string> = {};
  for (const key of ['trace_id', 'public_key', 'replay_id']) {
    const value = input[key];
    if (typeof value === 'string' && /^[\da-f]{32}$/i.test(value))
      output[key] = value;
  }
  for (const key of ['sample_rate', 'sample_rand']) {
    const value = input[key];
    if (typeof value === 'string' && /^(?:0(?:\.\d+)?|1(?:\.0+)?)$/.test(value))
      output[key] = value;
  }
  if (input['sampled'] === 'true' || input['sampled'] === 'false')
    output['sampled'] = input['sampled'];
  if (event.release) output['release'] = event.release;
  if (event.environment) output['environment'] = event.environment;
  if (typeof input['transaction'] === 'string')
    output['transaction'] = sentryRoute(input['transaction']);
  return { dynamicSamplingContext: output };
}

/** Final error boundary, after SDK processors and scope merging. */
export function filterSentryError(event: ErrorEvent): ErrorEvent {
  const tags: Record<string, string> = { app: 'brighthub' };
  Object.assign(
    tags,
    telemetryInternalIds(event.extra),
    telemetryInternalIds(event.tags),
  );
  const groups = event.tags?.['user_groups'];
  if (typeof groups === 'string') tags['user_groups'] = groups.slice(0, 4096);
  // SDK-generated replay linkage also triggers buffered replay-on-error capture.
  const replayId = event.tags?.['replayId'];
  if (typeof replayId === 'string' && /^[\da-f]{32}$/i.test(replayId))
    tags['replayId'] = replayId;
  if (event.tags?.['source'] === 'sentry_test') {
    tags['source'] = 'sentry_test';
    const run = event.tags['test_run_id'];
    if (typeof run === 'string' && uuid.test(run)) tags['test_run_id'] = run;
    const scenario = event.tags['test_scenario'];
    if (
      typeof scenario === 'string' &&
      ['angular_error', 'browser_error', 'unhandled_rejection'].includes(
        scenario,
      )
    )
      tags['test_scenario'] = scenario;
  }
  const trace = event.contexts?.trace;
  const extra = safeSentryExtras(event.extra);
  return {
    event_id: event.event_id,
    timestamp: event.timestamp,
    type: undefined,
    platform: event.platform,
    level: event.level,
    release: event.release,
    environment: event.environment,
    dist: event.dist,
    sdk: event.sdk,
    sdkProcessingMetadata: safeSentrySamplingMetadata(event),
    user: event.user
      ? {
          id: event.user.id,
          email: event.user.email,
          username: event.user.username,
        }
      : undefined,
    tags,
    extra,
    fingerprint:
      extra['method'] && extra['route_template']
        ? [
            '{{default}}',
            String(extra['method']),
            String(extra['route_template']),
            String(extra['http_status'] ?? 'unknown'),
          ]
        : undefined,
    contexts: trace
      ? {
          trace: {
            trace_id: trace.trace_id,
            span_id: trace.span_id,
            parent_span_id: trace.parent_span_id,
          },
        }
      : undefined,
    transaction: event.transaction ? sentryRoute(event.transaction) : undefined,
    message: event.message ? errorMessage(event.message) : undefined,
    exception: event.exception
      ? {
          values: event.exception.values?.map((exception) => ({
            type: types.has(exception.type ?? '') ? exception.type : 'Error',
            value: errorMessage(exception.value),
            stacktrace: exception.stacktrace
              ? {
                  frames: exception.stacktrace.frames?.map(stackFrame),
                }
              : undefined,
            mechanism: exception.mechanism
              ? {
                  type: [
                    'angular',
                    'auto.function.angular.error_handler',
                    'auto.browser.global_handlers.onerror',
                    'auto.browser.global_handlers.onunhandledrejection',
                    'onerror',
                    'onunhandledrejection',
                    'generic',
                    'chained',
                  ].includes(exception.mechanism.type)
                    ? exception.mechanism.type
                    : 'generic',
                  handled: exception.mechanism.handled,
                  exception_id: exception.mechanism.exception_id,
                  parent_id: exception.mechanism.parent_id,
                }
              : undefined,
          })),
        }
      : undefined,
    breadcrumbs: event.breadcrumbs
      ?.map(filterSentryBreadcrumb)
      .filter((value): value is Breadcrumb => value !== null),
    debug_meta: event.debug_meta
      ? {
          images: event.debug_meta.images?.flatMap((image) => {
            const codeFile = stackFile(image.code_file);
            return image.type === 'sourcemap' &&
              codeFile &&
              typeof image.debug_id === 'string' &&
              uuid.test(image.debug_id)
              ? [
                  {
                    type: 'sourcemap' as const,
                    code_file: codeFile,
                    debug_id: image.debug_id,
                  },
                ]
              : [];
          }),
        }
      : undefined,
  };
}
